import { useMemo } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { ALL_JOBS_ID } from '@v2/pages/common/constants/base.constants';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { ProductionOrderDetailsScreenName } from '@v2/pages/productionOrder/navigation/config';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { selectProductionOrderData, selectProductionOrderJobsList } from '@reducers/details';
import { ProductionOrderJobsListProps, ProductionOrderListJobId } from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderJobsList';
import { parseRouteParamAsNumber } from '@helpers';

export interface UseManageProductionOrderJobsListParams
  extends Pick<ProductionOrderJobsListProps, 'productionOrder' | 'activeLevelSubScreenName' | 'onListItemPress'> {}

export const useManageProductionOrderJobsList = ({
  productionOrder,
  activeLevelSubScreenName = LevelSubScreen.GENERAL,
  onListItemPress: onPressFromProps,
}: UseManageProductionOrderJobsListParams) => {
  const productionOrderJobsList = useSelector(selectProductionOrderJobsList);
  const productionOrders = useSelector(selectProductionOrderData);

  const route = useRoute();
  const navigation = useNavigation<any>();
  const jobId = parseRouteParamAsNumber(route.params?.selectedProductionOperationId);
  const perspective = route.params?.perspective;

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const jobHash = productionOrderJobsList?.data?.map((j) => j.productionOperationId)?.join(', ');

  const totalTimeForAllJobs = useMemo(() => {
    return productionOrderJobsList?.data?.reduce((acc: number, j) => acc + (j.totalTimeScheduledSeconds ?? 0), 0) ?? 0;
  }, [jobHash]);

  const noJobSelected = jobId == null;
  const allJobSelected = jobId === ALL_JOBS_ID;

  const onListItemPress = (jobId: ProductionOrderListJobId) => {
    if (onPressFromProps) {
      return onPressFromProps(jobId);
    }

    if (typeof jobId === 'number') {
      return navigateToMainDetailsScreen({
        mainScreen: JobDetailsScreenName,
        subScreen: JobDetailsScreen.JOBS,
        levelSubScreen: activeLevelSubScreenName,
        params: {
          perspective: Perspective.JOBS,
          productionOperationId: jobId,
          selectedProductionOperationId: jobId,
          productionOrderId: productionOrder.productionOrderId,
          keepList: true,
        },
      });
    }

    if (perspective === Perspective.ORDER && activeLevelSubScreenName === LevelSubScreen.GENERAL) {
      return navigation.setParams({ selectedProductionOperationId: jobId });
    }

    return navigateToMainDetailsScreen({
      mainScreen: ProductionOrderDetailsScreenName,
      subScreen: ProductionOrderDetailsScreen.ORDER,
      ...(jobId !== ALL_JOBS_ID ? { levelSubScreen: activeLevelSubScreenName } : {}),
      params: {
        perspective: Perspective.ORDER,
        productionOperationId: jobId,
        selectedProductionOperationId: jobId,
        productionOrderId: productionOrder.productionOrderId,
        keepList: true,
      },
    });
  };

  return {
    productionOrderJobsList,
    productionOrders,
    jobId,
    totalTimeForAllJobs,
    noJobSelected,
    allJobSelected,
    onListItemPress,
    navigation,
  };
};
